import { useCallback } from "react";

import { useAtom } from "jotai";

import clsx from "clsx";

import Tag from "ui-components/Tag";
import ButtonSwitch from "ui-components/Button-Switch";
import RadioSwitch from "ui-components/Radio-Button";

import {
  columnType as columnTypeAtom,
  deducedTypes as deducedTypesAtom,
} from "modules/data-files/api";
import { useAtomValue } from "jotai/utils";

interface Option {
  label: "NUMERIC" | "CATEGORICAL" | "NUM" | "CHAR";
  key: "numeric" | "char";
}

const typeButtons: Option[] = [
  { label: "NUMERIC", key: "numeric" },
  { label: "CATEGORICAL", key: "char" },
];

const abbrTypeButtons: Option[] = [
  { label: "NUM", key: "numeric" },
  { label: "CHAR", key: "char" },
];

interface PropsInterface {
  column: string;
  fileId: string;
  label?: string;
  showLabel?: boolean;
  renderAsButtons?: boolean;
  showShortButtons?: boolean;
  onChange?: (
    fileId: string,
    column: string,
    colType: "numeric" | "char"
  ) => any;
  className?: string;
}

const TypeSwitch = ({
  column,
  fileId,
  label,
  showLabel = true,
  renderAsButtons = false,
  showShortButtons = false,
  onChange,
  className,
}: PropsInterface) => {
  const deducedType = useAtomValue(deducedTypesAtom(fileId))[column];
  const [userDefinedType, setColumnType] = useAtom(
    columnTypeAtom({ fileId, column })
  );

  const handleChange = useCallback(
    (colType: "numeric" | "char") => {
      if (colType !== userDefinedType) {
        setColumnType(colType);
        onChange?.(fileId, column, colType);
      }
    },
    [fileId, column, userDefinedType, setColumnType, onChange]
  );

  if (!deducedType || !userDefinedType) {
    return null;
  }
  label = label || column;

  return (
    <>
      {deducedType === "int" ? (
        renderAsButtons ? (
          <ButtonSwitch
            options={showShortButtons ? abbrTypeButtons : typeButtons}
            current={userDefinedType as "numeric" | "char"}
            onClick={handleChange}
            size={showShortButtons ? "small" : "normal"}
            className={className}
          />
        ) : (
          <RadioSwitch
            options={showShortButtons ? abbrTypeButtons : typeButtons}
            selected={userDefinedType as "numeric" | "char"}
            onChange={handleChange}
            label={showLabel ? `${label}:` : undefined}
            className={className}
          />
        )
      ) : (
        <div className={clsx(className, "flex items-center")}>
          {showLabel && <b>{label}: </b>}
          <Tag bgColorClass="bg-primary-main" textColorClass="text-white">
            {deducedType}
          </Tag>
        </div>
      )}
    </>
  );
};

export default TypeSwitch;
